import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import useLearnerObjectUpdateCurrentOccupation from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateCurrentOccupation';
import useLearnerObjectUpdateEducationProfile from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateEducationProfile';
import useLearnerObjectUpdateInterestedCareers from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateInterestedCareers';
import useLearnerObjectUpdateLastIndicatedUnsureRolesAt from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateLastIndicatedUnsureRolesAt';
import useLearnerObjectUpdateLastIndicatedUnsureSkillsAt from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateLastIndicatedUnsureSkillsAt';
import useLearnerObjectUpdateLearningGoal from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateLearningGoal';
import useLearnerObjectUpdateSkillsWanted from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateSkillsWanted';
import type { QuestionValue } from 'bundles/onboarding-common/types';
import { mapAdvancerRoleValueForUserProfile } from 'bundles/onboarding-common/utils/userProfileUtils';

const useGetLearnerObjectPromise = () => {
  const { updateEducationProfile } = useLearnerObjectUpdateEducationProfile();

  const { updateLearningGoal } = useLearnerObjectUpdateLearningGoal();
  const { updateCurrentOccupation } = useLearnerObjectUpdateCurrentOccupation();
  const { updateSkillsWanted } = useLearnerObjectUpdateSkillsWanted();
  const { updateInterestedCareers } = useLearnerObjectUpdateInterestedCareers();
  const { updateLastIndicatedUnsureSkillsAt } = useLearnerObjectUpdateLastIndicatedUnsureSkillsAt();
  const { updateLastIndicatedUnsureRolesAt } = useLearnerObjectUpdateLastIndicatedUnsureRolesAt();

  const getLearnerObjectPromise = async ({ questionValue }: { questionValue: QuestionValue | null | undefined }) => {
    switch (questionValue?.__typename) {
      case QUESTION_IDS.learningGoal: {
        if (questionValue.value) {
          return updateLearningGoal(questionValue.value);
        }
        return Promise.resolve();
      }
      case QUESTION_IDS.roleSelect: {
        const isUnsureOfRoles = questionValue.value?.some((role) => role.type === 'UNSURE') ?? false;

        if (isUnsureOfRoles) {
          const timestamp = new Date().toISOString();
          return updateLastIndicatedUnsureRolesAt(timestamp);
        }

        if (questionValue.value) {
          return updateInterestedCareers(questionValue.value);
        }

        return Promise.resolve();
      }
      case QUESTION_IDS.roleSelectAdvancer: {
        const { occupationPreferences, currentOccupation, isUnsureOfRoles } = mapAdvancerRoleValueForUserProfile(
          questionValue.value
        );

        if (isUnsureOfRoles) {
          // Update LearnerObject timestamp when user is unsure
          const timestamp = new Date().toISOString();
          return updateLastIndicatedUnsureRolesAt(timestamp);
        }

        if (questionValue.value) {
          // Update current occupation first (best effort - for data consistency only)
          const currentOccupationResult = await updateCurrentOccupation(currentOccupation ?? { id: '' });

          // Always update interested careers (this is the primary data)
          const interestedCareersResult = await updateInterestedCareers(occupationPreferences);

          // Success is based only on interestedCareers since currentOccupation is supplementary
          // Include currentOccupation errors for visibility, but don't fail the request
          return {
            success: interestedCareersResult.success,
            errors: [...currentOccupationResult.errors, ...interestedCareersResult.errors],
          };
        }

        return Promise.resolve();
      }
      case QUESTION_IDS.skillSelect: {
        const isUnsureOfSkills = questionValue.value?.some((skill) => skill.type === 'UNSURE');

        if (isUnsureOfSkills) {
          const timestamp = new Date().toISOString();
          return updateLastIndicatedUnsureSkillsAt(timestamp);
        }

        if (questionValue.value) {
          return updateSkillsWanted(questionValue.value);
        }

        return Promise.resolve();
      }
      case QUESTION_IDS.jobTitle: {
        if (questionValue.value) {
          return updateCurrentOccupation(questionValue.value);
        }
        return Promise.resolve();
      }
      case QUESTION_IDS.educationLevel: {
        if (questionValue.value) {
          return updateEducationProfile(questionValue.value);
        }
        return Promise.resolve();
      }
      default:
        return Promise.resolve();
    }
  };

  return { getLearnerObjectPromise };
};

export default useGetLearnerObjectPromise;
